import type { GraphQLResult } from 'aws-amplify/api'
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query'
import { Ownership } from '@skop-app/lib-domain/types'
import type {
  Order,
  OrderModel,
  OrderQueryResult,
} from '@skop-app/lib-domain/types'
import { sortDate } from '@skop-app/lib-domain/utils'

import {
  getOrderByIDQuery,
  ordersByManagerQuery,
  ordersByOrganizationQuery,
  ordersByShopQuery,
  ordersByToOrganizationQuery,
  ordersByToUserQuery,
  ordersByUserQuery,
} from '@/services/graphql/orders'
import { orderAdapter } from '@/services/adapters/orders'
import { getAmplifyClient } from '@/services/amplifyClient'
import { transactionalQueryPreset } from '@/services/query-presets'

const client = getAmplifyClient()

export const useOrder = (orderID?: string) => {
  return useQuery({
    queryKey: ['order', orderID],
    queryFn: () => getOrderByID(orderID),
    enabled: !!orderID,
    select: (data) => data && orderAdapter(data),
  })
}

const getOrderByID = async (orderID?: string) => {
  const { data } = (await client.graphql({
    query: getOrderByIDQuery,
    variables: { orderID },
  })) as GraphQLResult<OrderQueryResult>
  return data?.getOrder
}

export type ShopOrderInput = {
  shopID: string | undefined
  createdAt?: {
    ge?: string
    lt?: string
  }
  filter: {
    type?: { eq?: 'CART' | 'FAVORITE' | 'QUOTE' }
    status?: {
      ne?: 'PENDING' | 'DELETED'
    }
    updatedAt?: {
      ge?: string
    }
    or?: any[]
  }
  limit?: number
  nextToken?: string | null
}

export type UserOrderInput = {
  type: 'FAVORITE' | 'CART' | 'QUOTE'
  orderUserId: { eq: string | undefined }
  createdAt?: {
    ge?: string
    lt?: string
  }
  filter?: {
    status?: {
      ne?: 'PENDING' // NOT SENT YET
    }
  }
  limit?: number
  nextToken?: string | null
}

export type QuoteOrderInput = {
  type: 'QUOTE'
  orderUserId?: { eq: string | undefined }
  createdAt?: {
    ge?: string
    lt?: string
  }
  filter?: {
    or?: (
      | { managerID: { eq: string | undefined } }
      | { organizationID: { eq: string | undefined } }
    )[]
    status?: {
      ne?: 'DELETED'
    }
    requestType?: {
      eq?: 'quote' | 'quoteOrder'
    }
  }
  limit?: number
  nextToken?: string | null
}

export type OrderPeriod = {
  label: string
  period: string
  value: number | null
}

export type PageSettings = {
  view: 'sent' | 'buyer' | 'sentquote' | 'request' | 'seller'
  title: string
  description: string
  StatsComponent?: React.ComponentType<{
    ownership: Ownership
    orders: Order[]
    isLoading: boolean
    period: OrderPeriod
    setPeriod: React.Dispatch<React.SetStateAction<OrderPeriod>>
  }>
  queryParams: {
    enabled: boolean
    input?: {
      type?: 'FAVORITE' | 'CART' | 'QUOTE'
      orderUserId?: { eq: string | undefined }
      createdAt?: {
        ge?: string
        lt?: string
      }
      filter?: {
        status?: { ne?: string }
        or?: any[]
      }
    }
  }
}

const ordersByShop = async ({
  index,
  filter,
  createdAt,
  limit,
  nextToken,
}: {
  index: string
  filter: Record<string, unknown>
  createdAt?: { ge?: string; lt?: string }
  limit: number
  nextToken: string | null
}) => {
  try {
    const { data } = (await client.graphql({
      query: ordersByShopQuery,
      variables: { shopID: index, createdAt, filter, limit, nextToken },
    })) as GraphQLResult<OrdersByShopResults>
    return {
      items: data?.ordersByShop?.items || [],
      nextToken: data?.ordersByShop?.nextToken || null,
      nextTokenKey: 'shopID',
    }
  } catch (e) {
    console.error(e)
    return { items: [], nextToken: null, nextTokenKey: 'shopID' }
  }
}

const ordersByUser = async ({
  index,
  filter,
  createdAt,
  limit,
  nextToken,
}: {
  index: string
  filter: Record<string, unknown>
  createdAt?: { ge?: string; lt?: string }
  limit: number
  nextToken: string | null
}) => {
  try {
    const { data } = (await client.graphql({
      query: ordersByUserQuery,
      variables: { userID: index, createdAt, filter, limit, nextToken },
    })) as GraphQLResult<OrdersByUserResults>
    return {
      items: data?.ordersByUser?.items || [],
      nextToken: data?.ordersByUser?.nextToken || null,
      nextTokenKey: 'orderUserId',
    }
  } catch (e) {
    console.error(e)
    return { items: [], nextToken: null, nextTokenKey: 'orderUserId' }
  }
}

const ordersByToOrganization = async ({
  index,
  filter,
  createdAt,
  limit,
  nextToken,
}: {
  index: string
  filter: Record<string, unknown>
  createdAt?: { ge?: string; lt?: string }
  limit: number
  nextToken: string | null
}) => {
  try {
    const { data } = (await client.graphql({
      query: ordersByToOrganizationQuery,
      variables: {
        toOrganizationID: index,
        createdAt,
        filter,
        limit,
        nextToken,
      },
    })) as GraphQLResult<OrdersByToOrganizationResults>
    return {
      items: data?.ordersByToOrganization?.items || [],
      nextToken: data?.ordersByToOrganization?.nextToken || null,
      nextTokenKey: 'toOrganizationID',
    }
  } catch (e) {
    console.error(e)
    return { items: [], nextToken: null, nextTokenKey: 'toOrganizationID' }
  }
}

const ordersByOrganization = async ({
  index,
  filter,
  createdAt,
  limit,
  nextToken,
}: {
  index: string
  filter: Record<string, unknown>
  createdAt?: { ge?: string; lt?: string }
  limit: number
  nextToken: string | null
}) => {
  try {
    const { data } = (await client.graphql({
      query: ordersByOrganizationQuery,
      variables: { organizationID: index, createdAt, filter, limit, nextToken },
    })) as GraphQLResult<OrdersByOrganizationResults>
    return {
      items: data?.ordersByOrganization?.items || [],
      nextToken: data?.ordersByOrganization?.nextToken || null,
      nextTokenKey: 'organizationID',
    }
  } catch (e) {
    console.error(e)
    return { items: [], nextToken: null, nextTokenKey: 'organizationID' }
  }
}

const ordersByManager = async ({
  index,
  filter,
  createdAt,
  limit,
  nextToken,
}: {
  index: string
  filter: Record<string, unknown>
  createdAt?: { ge?: string; lt?: string }
  limit: number
  nextToken: string | null
}) => {
  try {
    const { data } = (await client.graphql({
      query: ordersByManagerQuery,
      variables: { managerID: index, createdAt, filter, limit, nextToken },
    })) as GraphQLResult<OrdersByManagerResults>
    return {
      items: data?.ordersByManager?.items || [],
      nextToken: data?.ordersByManager?.nextToken || null,
      nextTokenKey: 'managerID',
    }
  } catch (e) {
    console.error(e)
    return { items: [], nextToken: null, nextTokenKey: 'managerID' }
  }
}

const ordersByToUser = async ({
  index,
  filter,
  createdAt,
  limit,
  nextToken,
}: {
  index: string
  filter: Record<string, unknown>
  createdAt?: { ge?: string; lt?: string }
  limit: number
  nextToken: string | null
}) => {
  try {
    const { data } = (await client.graphql({
      query: ordersByToUserQuery,
      variables: { toUserID: index, createdAt, filter, limit, nextToken },
    })) as GraphQLResult<OrdersByToUserResults>
    return {
      items: data?.ordersByToUser?.items || [],
      nextToken: data?.ordersByToUser?.nextToken || null,
      nextTokenKey: 'toUserID',
    }
  } catch (e) {
    console.error(e)
    return { items: [], nextToken: null, nextTokenKey: 'toUserID' }
  }
}

/**
 * Pure helpers for `useListRequests` multi-index pagination.
 * Lives in its own module so tests assert the same code the hook runs.
 */

export type OrdersListPageParam = { [key: string]: string | null } | null

/**
 * `queryEntries` from `Object.entries(queryObject ?? {})`, then the same
 * `activeEntries` branch as in `useListRequests`.
 */
export function getActiveOrderQueryEntries(
  queryObject: { [key: string]: string } | undefined,
  pageParam: OrdersListPageParam,
): Array<[string, string]> {
  const queryEntries = Object.entries(queryObject ?? {})
  if (!pageParam) {
    return queryEntries
  }
  return queryEntries.filter(([key]) => pageParam[key] !== null)
}

/**
 * Merged `nextToken` map after one page of parallel fetches (see `useListRequests`).
 */
export function mergeOrderListNextTokens(
  queryEntries: Array<[string, string]>,
  results: Array<{ nextToken: string | null; nextTokenKey: string }>,
): { [key: string]: string | null } {
  return {
    ...Object.fromEntries(queryEntries.map(([key]) => [key, null])),
    ...results.reduce(
      (acc, { nextToken, nextTokenKey }) => ({
        ...acc,
        [nextTokenKey]: nextToken ?? null,
      }),
      {} as { [key: string]: string | null },
    ),
  }
}

/**
 * Per-source `nextToken` passed into each `ordersBy*` call.
 */
export function nextTokenForOrderQueryKey(
  key: string,
  pageParam: OrdersListPageParam,
): string | null {
  return (pageParam?.[key] as string | null | undefined) ?? null
}

const getOrdersFunctions = {
  shopID: ordersByShop,
  toOrganizationID: ordersByToOrganization,
  orderUserId: ordersByUser,
  organizationID: ordersByOrganization,
  managerID: ordersByManager,
  toUserID: ordersByToUser,
}

export const useListRequests = ({
  enabled = false,
  input,
  queryObject,
}: {
  enabled: boolean
  input?: PageSettings['queryParams']['input'] & { limit?: number }
  queryObject?: { [key: string]: string }
}) => {
  return useInfiniteQuery({
    queryKey: ['orders', input, queryObject],
    queryFn: async ({ pageParam }) => {
      const limit = input?.limit || 100
      const queryEntries = Object.entries(queryObject ?? {})
      const activeEntries = getActiveOrderQueryEntries(queryObject, pageParam)

      if (activeEntries.length === 0) {
        return {
          items: [],
          nextToken: Object.fromEntries(
            queryEntries.map(([key]) => [key, null]),
          ) as { [key: string]: string | null },
        } satisfies {
          items: OrderModel[]
          nextToken: { [key: string]: string | null }
        }
      }

      const results = await Promise.all(
        activeEntries.map(([key, value]) =>
          getOrdersFunctions[key as keyof typeof getOrdersFunctions]({
            index: value as string,
            createdAt: input?.createdAt,
            filter: input?.filter ?? {},
            limit,
            nextToken: nextTokenForOrderQueryKey(key, pageParam),
          }),
        ),
      )

      return {
        items: results.flatMap((result) => result.items),
        nextToken: mergeOrderListNextTokens(queryEntries, results),
      } satisfies {
        items: OrderModel[]
        nextToken: { [key: string]: string | null }
      }
    },
    enabled: !!enabled && !!input,
    initialPageParam: null,
    getNextPageParam: (lastPage) => {
      return Object.values(lastPage.nextToken).some((value) => value !== null)
        ? lastPage.nextToken
        : null
    },
    select: (data) => {
      const orders = data?.pages?.flatMap((page) => page.items) ?? []
      return sortDate(orders.map(orderAdapter), 'createdAt', 'DESC')
    },
    placeholderData: keepPreviousData,
    ...transactionalQueryPreset,
  })
}

type OrdersByShopResults = {
  ordersByShop: {
    items: OrderModel[]
    nextToken?: string | null
  }
}

type OrdersByUserResults = {
  ordersByUser: {
    items: OrderModel[]
    nextToken?: string | null
  }
}

type OrdersByToOrganizationResults = {
  ordersByToOrganization: {
    items: OrderModel[]
    nextToken?: string | null
  }
}

type OrdersByOrganizationResults = {
  ordersByOrganization: {
    items: OrderModel[]
    nextToken?: string | null
  }
}

type OrdersByManagerResults = {
  ordersByManager: {
    items: OrderModel[]
    nextToken?: string | null
  }
}

type OrdersByToUserResults = {
  ordersByToUser: {
    items: OrderModel[]
    nextToken?: string | null
  }
}
